import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Avatar,
  Grid,
  IconButton,
  InputAdornment,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Edit, LocationOn, Flag } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { isEmpty } from "lodash";
import {
  getAPIProgressData,
  getApiData,
  getApiDataWith403Handling,
} from "../../utils/APIHelper";
import { Setting } from "../../utils/Setting";
import { updateStepMappingToCompleted } from "../../utils/stepMappingUtils";
import authActions from "../../redux/reducers/auth/actions";
import SearchLocation from "../NewCreateProposal/components/Summary/SearchLocation";
import CInput from "../../components/CInput";
import CPhoneInput from "../../components/CPhoneInput";
import ProfilePhotoModal from "../../components/ProfilePhotoModal";
import { ReactComponent as LeftArrowIcon } from "../../assets/svg/LeftArrowIcon.svg";

export default function EditProfileForm() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const { setUserData } = authActions;
  const { token, userData } = useSelector((state) => state.auth);
  
  const [buttonLoader, setButtonLoader] = useState(false);
  const [pageLoad, setPageLoad] = useState(true);
  const [profileData, setProfileData] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState({});
  const [userLocation, setUserLocation] = useState("");
  const [bLogo, setBLogo] = useState(null);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    businessLogo: "",
    cname: "",
    address: "",
    email: "",
    website: "",
    phone: "",
    countryCode: "",
    pCode: "",
    businessYear: "",
    employees: "",
    annualContract: "",
    description: "",
    username: "",
    type: "",
  });

  const [errors, setErrors] = useState({
    addErr: false,
    addMsg: "",
    webErr: false,
    webMsg: "",
    yearErr: false,
    yearMsg: "",
    employeeErr: false,
    employeeMsg: "",
    contractErr: false,
    contarctMsg: "",
    roleErr: false,
    roleMsg: "",
    websiteErr: false,
    websiteMsg: "",
  });

  const data = profileData?.contractor_data;
  const isEdit = !isEmpty(profileData);

  useEffect(() => {
    getUserDetailsByIdApiCall();
  }, []);

  useEffect(() => {
    if (isEdit && profileData?.contractor_data) {
      const contractorData = profileData.contractor_data;
      
      // Map API data to form fields
      setFormData({
        businessLogo: profileData?.profile_url || "",
        cname: contractorData?.company_name || "",
        address: contractorData?.company_address || "",
        email: profileData?.email || "",
        website: contractorData?.website || "",
        phone: profileData?.phone_no || "",
        countryCode: "",
        pCode: profileData?.phone_code || "",
        businessYear: contractorData?.total_years_in_business || "",
        employees: contractorData?.company_size || "",
        annualContract: contractorData?.no_of_contracts_annually || "",
        description: contractorData?.description || "",
        username: profileData?.username || "",
        type: contractorData?.type || userData?.role || "",
      });

      // Set location data from API
      if (contractorData?.lat && contractorData?.long) {
        let locationObj = {
          lat: contractorData.lat.toString(),
          long: contractorData.long.toString(),
          location: contractorData?.company_address || "",
          city: contractorData?.city || ""
        };
        setSelectedLocation(locationObj);
        setUserLocation(locationObj?.location);
      }
    }
  }, [profileData, isEdit]);

  useEffect(() => {
    if (formData?.businessLogo && typeof formData?.businessLogo === "object") {
      const imgUrl = URL.createObjectURL(formData?.businessLogo);
      setBLogo(imgUrl);
    } else {
      setBLogo(formData?.businessLogo || "");
    }
  }, [formData?.businessLogo]);

  async function getUserDetailsByIdApiCall() {
    setPageLoad(true);
    try {
      const response = await getApiData(
        Setting.endpoints.me,
        "GET",
        null,
        { Authorization: `Bearer ${token}` }
      );
      if (response.success) {
        dispatch(setUserData(response?.data));
        setProfileData(response?.data);
      } else {
        toast.error("Failed to load profile data");
      }
      setPageLoad(false);
    } catch (error) {
      console.log("🚀 ~ file: index.js:63 ~ by id api ~ error:", error);
      toast.error("Failed to load profile data");
      setPageLoad(false);
    }
  }

  function validation() {
    const error = { ...errors };
    let valid = true;
    let scroll = false;
    let section = null;

    // Company name validation removed - field is now disabled

    if (formData.description && formData.description.length > 1000) {
      valid = false;
      error.descriptionErr = true;
      error.descriptionMsg = "Description should not be greater than 1000 characters";
      if (!scroll) {
        scroll = true;
        section = document.querySelector("#description");
      }
    }

    if (isEmpty(userLocation)) {
      valid = false;
      error.addErr = true;
      error.addMsg = "Please Enter Address";
      if (!scroll) {
        scroll = true;
        section = document.querySelector("#Address");
      }
    }

    if (isEmpty(formData.businessYear)) {
      toast.error("Please select the number of business years");
      valid = false;
    }

    if (userData?.role !== "designer" && isEmpty(formData.employees)) {
      toast.error("Please select the number of employees");
      valid = false;
    }

    if (isEmpty(formData.annualContract)) {
      valid = false;
      error.contractErr = true;
      error.contarctMsg = "Please Enter No. of Contracts Annually";
      if (!scroll) {
        scroll = true;
        section = document.querySelector("#contract");
      }
    } else if (formData?.annualContract < 0) {
      valid = false;
      error.contractErr = true;
      error.contarctMsg = "Please Enter Valid No. of Contracts Annually";
      if (!scroll) {
        scroll = true;
        section = document.querySelector("#contract");
      }
    }

    // Role validation removed - field is now disabled

    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setErrors(error);

    if (valid) {
      editContractorDetailsApiCall();
    }
  }

  async function updateUserProfileApiCall() {
    try {
      const usernameToSend = formData.username || profileData?.username || "";
      
      const updateData = {
        username: usernameToSend,
      };
      
      if (formData.phone) updateData.phone_no = formData.phone;
      if (formData.pCode) updateData.phone_code = formData.pCode;

      const response = await getApiData(
        Setting.endpoints.updateProfile,
        "POST",
        updateData,
        { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      );

      if (response.success) {
        console.log("User profile updated successfully:", response);
        return true;
      } else {
        console.log("Failed to update user profile:", response);
        toast.error(response.message || "Failed to update user profile");
        return false;
      }
    } catch (error) {
      console.log("Error updating user profile:", error);
      toast.error("Failed to update user profile");
      return false;
    }
  }

  async function editContractorDetailsApiCall() {
    try {
      setButtonLoader(true);
      
      const userProfileUpdated = await updateUserProfileApiCall();
      if (!userProfileUpdated) {
        setButtonLoader(false);
        return;
      }

      let data = {
        description: formData?.description ? formData?.description : "",
        company_address: userLocation ? userLocation : "",
        website: formData?.website ? formData?.website : "",
        total_years_in_business: formData?.businessYear ? formData?.businessYear : "",
        company_size: formData?.employees ? formData?.employees : "",
        no_of_contracts_annually: formData?.annualContract ? parseInt(formData?.annualContract) : 0,
        contractor_expertise: userData?.contractor_data?.contractor_expertise || "",
        linkedin_url: userData?.contractor_data?.linkedin_url || "",
        fb_url: userData?.contractor_data?.fb_url || "",
        insta_url: userData?.contractor_data?.insta_url || "",
        lat: selectedLocation?.lat ? parseFloat(selectedLocation?.lat) : null,
        long: selectedLocation?.long ? parseFloat(selectedLocation?.long) : null,
        city: selectedLocation?.city || "",
        type: formData?.type || "contractor",
        projects_key_locations: userData?.contractor_data?.projects_key_locations || "",
        company_name: userData?.contractor_data?.company_name || "",
      };
      console.log("API payload:", data);

      const response = await getAPIProgressData(
        Setting.endpoints.addContractorDetails,
        "POST",
        data,
        true,
        { Authorization: `Bearer ${token}` }
      );
      
      if (response.success) {
        await updateStepMappingToCompleted(userData?.id || profileData?.id, "CREATE_ACCOUNT", token);
        
        await getUserDetailsByIdApiCall();
        
        toast.success("Profile updated successfully");
        navigate(-1);
      } else {
        toast.error(response.message);
      }
      setButtonLoader(false);
    } catch (error) {
      console.log("🚀 ~ file: index.js:63 ~ loginUser ~ error:", error);
      setButtonLoader(false);
      toast.error(error.toString());
    }
  }

  async function initiateContractorOnboardingWorkflow() {
    try {
      const refId = userData?.id || profileData?.id;
      
      if (!refId) {
        console.log("No valid refId found for contractor onboarding workflow");
        return;
      }
      
      const stepsMapData = {
        refId: parseInt(refId),
        refType: "contractor",
        workflowName: "CONTRACTOR_ONBOARDING"
      };
      
      const response = await getApiData(
        Setting.endpoints.stepsMap,
        "POST",
        stepsMapData,
        { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        true // Use new base URL for steps mapping
      );
      
      if (response.success) {
        // Now update the CREATE_ACCOUNT step to COMPLETED
        await updateStepMappingToCompleted(refId, "CREATE_ACCOUNT", token);
        
        toast.success("Getting started process initiated successfully");
      } else {
        console.log("Failed to initiate contractor onboarding workflow:", response);
        // Don't show error toast as this is not critical for profile update
      }
    } catch (error) {
      console.log("Error initiating contractor onboarding workflow:", error);
      console.log("Error details:", error.message, error.response);
      
      // Handle specific error cases
      if (error.message && error.message.includes("WORKFLOW_ALREADY_EXISTS")) {
        console.log("Workflow already exists for this contractor - this is expected behavior");
        // Don't show error toast as this is not critical for profile update
      } else {
        console.log("Unexpected error during workflow initiation");
        // Don't show error toast as this is not critical for profile update
      }
    }
  }


  // Website validation function
  const validateWebsite = (url) => {
    if (!url) return { isValid: true, message: "" };
    
    // Basic URL format validation
    const urlRegex = /^https?:\/\/(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/.*)?$/;
    
    if (!urlRegex.test(url)) {
      return { 
        isValid: false, 
        message: "Invalid website URL. Must include http:// or https:// and a valid domain" 
      };
    }
    
    return { isValid: true, message: "" };
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Validate website field
    if (field === "website") {
      const validation = validateWebsite(value);
      setErrors(prev => ({
        ...prev,
        websiteErr: !validation.isValid,
        websiteMsg: validation.message
      }));
    }
  };

  const handleLocationChange = (locationObj) => {
    console.log("Location selected:", locationObj);
    // Map PlaceAutoComplete data structure to our expected structure
    const mappedLocation = {
      lat: locationObj?.lat,
      long: locationObj?.lng, // PlaceAutoComplete uses 'lng', we need 'long'
      location: locationObj?.location,
      city: locationObj?.city
    };
    console.log("Mapped location:", mappedLocation);
    setSelectedLocation(mappedLocation);
    setUserLocation(locationObj?.location);
    setFormData(prev => ({ ...prev, address: locationObj?.location }));
  };

  // Every field on this form was a hardcoded 400px box, so each row overflowed
  // a phone by roughly double. Rows stack and fields fill instead, capped at
  // the original 400px from md up so the desktop layout is unchanged.
  const fieldRowSx = {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    gap: { xs: 2.5, md: 3 },
    alignItems: "flex-start",
  };

  const fieldSx = {
    width: "100%",
    maxWidth: { xs: "100%", md: 400 },
  };

  // `.8vw` of horizontal padding is 3px on a phone, which collapsed these
  // pills to unreadable slivers. Fixed px below md, original vw above.
  const optionPillSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 500,
    fontSize: { xs: "13px", md: "12px" },
    padding: { xs: "8px 14px", md: "1vh .8vw" },
    minHeight: { xs: 38, md: "auto" },
    border: "1px solid #D1D5DB",
    whiteSpace: "nowrap",
  };

  const businessYearsOptions = [
    "0-1 year",
    "2-3 years", 
    "4-7 years",
    "8-15 years",
    "16+ years"
  ];

  const companySizeOptions = [
    "1-10 employees",
    "11-50 employees",
    "51-200 employees",
    "200+ employees"
  ];

  // Defined once and arranged two ways below, so the mobile regrouping cannot
  // drift from the desktop layout.
  const avatarBlock = (
    <Box sx={{ position: "relative", display: "inline-block", flexShrink: 0 }}>
      <Avatar
        src={formData.businessLogo || bLogo}
        sx={{
          width: { xs: 88, md: 100 },
          height: { xs: 88, md: 100 },
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          fontSize: "2rem",
        }}
      >
        {formData.username?.charAt(0) || "U"}
      </Avatar>
      <IconButton
        onClick={() => setPhotoModalOpen(true)}
        sx={{
          position: "absolute",
          bottom: 0,
          right: 0,
          backgroundColor: "white",
          border: "2px solid white",
          width: 32,
          height: 32,
          "&:hover": {
            backgroundColor: "#f5f5f5",
          },
        }}
      >
        <Edit sx={{ fontSize: 16, color: "#4D6BF6" }} />
      </IconButton>
    </Box>
  );

  const usernameField = (
    <CInput
      label="Username*"
      value={formData.username || ""}
      onChange={(e) => {
        handleInputChange("username", e.target.value);
      }}
      placeholder="Enter your username"
    />
  );

  const roleField = (
    <CInput
      label="Role"
      value={formData.type ? formData.type.charAt(0).toUpperCase() + formData.type.slice(1) : ""}
      disabled={true}
      placeholder="Role"
    />
  );

  const emailField = (
    <CInput
      label="Email Address"
      value={formData.email}
      onChange={(e) => handleInputChange("email", e.target.value)}
      placeholder="Enter your email address"
      type="email"
    />
  );

  const phoneField = (
    <CPhoneInput
      label="WhatsApp Number"
      placeholder="Enter your WhatsApp number"
      state={formData}
      setState={(newState) => {
        setFormData(prev => ({
          ...prev,
          ...newState
        }));
      }}
    />
  );

  // Mobile: the avatar pairs with Username and Role stacked beside it, then
  // Email and WhatsApp run full width underneath. Desktop keeps the avatar
  // alongside a 2x2 field grid, unchanged.
  const personalInfoLayout = isMobile ? (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {avatarBlock}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {usernameField}
          {roleField}
        </Box>
      </Box>
      {emailField}
      {phoneField}
    </Box>
  ) : (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
      {avatarBlock}
      <Box sx={{ flex: 1, width: "100%", minWidth: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={fieldRowSx}>
            <Box sx={fieldSx}>{usernameField}</Box>
            <Box sx={fieldSx}>{roleField}</Box>
          </Box>
          <Box sx={fieldRowSx}>
            <Box sx={fieldSx}>{emailField}</Box>
            <Box sx={fieldSx}>{phoneField}</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  if (pageLoad) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress size={30} />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#fff", minHeight: "100vh" }}>
      {/* Gradient Header. On mobile it sticks to the top so Save Changes
          stays reachable without scrolling back up a long form. */}
      <Box
        sx={{
          padding: { xs: "16px", md: "4vh 2vw 2vh 2vw" },
          background:
            "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
          position: { xs: "sticky", md: "static" },
          top: 0,
          zIndex: 5,
          backgroundColor: { xs: "#fff", md: "transparent" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, md: 2 }, minWidth: 0 }}>
            {/* Same back control the project-details header uses, so the two
                screens open the same way. */}
            <LeftArrowIcon
              onClick={() => navigate(-1)}
              style={{ cursor: "pointer", flexShrink: 0 }}
            />
            <Typography
              sx={{
                fontSize: { xs: "18px", md: "20px" },
                fontWeight: "600",
                color: "#0D0D0E",
                fontFamily: "Inter Tight",
                letterSpacing: "0",
              }}
            >
              Edit Profile
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={validation}
            disabled={buttonLoader}
            disableElevation
            sx={{
              backgroundColor: "#4D6BF6",
              borderRadius: 2,
              textTransform: "none",
              px: 2,
              py: 0.75,
              height: "fit-content",
              flexShrink: 0,
              whiteSpace: "nowrap",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {buttonLoader ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </Box>


      {/* Form Content */}
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Personal Information */}
        <Box sx={{ mb: { xs: 3.5, md: 4 } }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: { xs: 2, md: 3 }, color: "#0d0d0e", letterSpacing: 0, fontSize: { xs: "17px", md: "20px" } }}>
            Personal Information
          </Typography>

          {personalInfoLayout}
        </Box>

        {/* Company Information */}
        <Box sx={{ mb: { xs: 3.5, md: 4 } }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: { xs: 2, md: 3 }, color: "#0d0d0e", letterSpacing: 0, fontSize: { xs: "17px", md: "20px" } }}>
            Company Information
          </Typography>

          <Box sx={fieldRowSx}>
            <Box sx={fieldSx}>
              <CInput
                label="Company Name"
                placeholder="Enter your company name"
                value={formData.cname}
                onChange={(e) => handleInputChange("cname", e.target.value)}
                disabled={true}
              />
            </Box>

            <Box sx={fieldSx}>
              <CInput
                label="Company Website"
                placeholder="Enter your company website (e.g., https://example.com)"
                value={formData.website}
                onChange={(e) => handleInputChange("website", e.target.value)}
                type="url"
                error={errors.websiteErr}
                helperText={errors.websiteMsg}
              />
            </Box>
          </Box>
        </Box>

        {/* Location */}
        <Box sx={{ mb: { xs: 3.5, md: 4 } }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: { xs: 2, md: 3 }, color: "#0d0d0e", letterSpacing: 0, fontSize: { xs: "17px", md: "20px" } }}>
            Location
          </Typography>

          <Grid container spacing={1}>
            <Grid item xs={12} md={6}>
              <Box sx={fieldSx}>
                {/* Same picker as the create-profile screen. It resolves
                    addresses through our own backend rather than the Google
                    Maps JS API, so it keeps working when the Maps key is
                    unavailable. */}
                <SearchLocation
                  details={{ location: userLocation || data?.company_address || "" }}
                  setDetails={(updater) => {
                    const next =
                      typeof updater === "function"
                        ? updater({
                            location:
                              userLocation || data?.company_address || "",
                          })
                        : updater;
                    handleLocationChange({ location: next?.location || "" });
                  }}
                  errObj={{
                    locationErr: errors.addErr,
                    locationMsg: errors.addMsg,
                  }}
                  setErrObj={() =>
                    setErrors((prev) => ({
                      ...prev,
                      addErr: false,
                      addMsg: "",
                    }))
                  }
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Total Years in Business */}
        <Box sx={{ mb: { xs: 3.5, md: 4 } }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: { xs: 2, md: 3 }, color: "#0d0d0e", letterSpacing: 0, fontSize: { xs: "17px", md: "20px" } }}>
            Total years in business
          </Typography>

          <Box sx={{ display: "flex", gap: { xs: 1, md: 2 }, flexWrap: "wrap" }}>
            {businessYearsOptions.map((option) => (
              <Button
                key={option}
                variant={formData.businessYear === option ? "contained" : "outlined"}
                onClick={() => handleInputChange("businessYear", option)}
                sx={{
                  ...optionPillSx,
                  backgroundColor: formData.businessYear === option ? "#1F2937" : "white",
                  color: formData.businessYear === option ? "white" : "#6B7280",
                  "&:hover": {
                    backgroundColor: formData.businessYear === option ? "#374151" : "#F9FAFB",
                    border: "1px solid #D1D5DB",
                  },
                }}
              >
                {option}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Company Size */}
        {userData?.role !== "designer" && (
          <Box sx={{ mb: { xs: 3.5, md: 4 } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: { xs: 2, md: 3 }, color: "#0d0d0e", letterSpacing: 0, fontSize: { xs: "17px", md: "20px" } }}>
              Company size*
            </Typography>

            <Box sx={{ display: "flex", gap: { xs: 1, md: 2 }, flexWrap: "wrap" }}>
              {companySizeOptions.map((option) => (
                <Button
                  key={option}
                  variant={formData.employees === option ? "contained" : "outlined"}
                  onClick={() => handleInputChange("employees", option)}
                  sx={{
                    ...optionPillSx,
                    backgroundColor: formData.employees === option ? "#1F2937" : "white",
                    color: formData.employees === option ? "white" : "#6B7280",
                    "&:hover": {
                      backgroundColor: formData.employees === option ? "#374151" : "#F9FAFB",
                      border: "1px solid #D1D5DB",
                    },
                  }}
                >
                  {option}
                </Button>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Profile Photo Upload Modal */}
      <ProfilePhotoModal
        visible={photoModalOpen}
        handleClose={() => setPhotoModalOpen(false)}
        currentPhoto={formData.businessLogo || bLogo}
        username={formData.username}
        onUploadSuccess={(updatedUserData) => {
          setProfileData(updatedUserData);
          setFormData(prev => ({
            ...prev,
            businessLogo: updatedUserData?.profile_url || "",
          }));
        }}
      />
    </Box>
  );
}
